import { FormProvider, useForm } from "react-hook-form";
import { Button, Loading } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useAddTimeOfDayMultiplierMutation } from "@/redux/apis/config/pricing/timeOfDay";
import { NameField } from "./fields/NameField";
import { CostMultiplierField } from "./fields/CostMultiplierField";
import { StatusField } from "./fields/StatusField";
import { TreatAsSpecialEventField } from "./fields/TreatAsSpecialEventField";
import { TimeDateField } from "./fields/TimeDateField";
import { zodResolver } from "@hookform/resolvers/zod";
import { timeAndDayFormSchema } from "../fixtures";
import { TimeAndDayFormData } from "../types";
import { addToast } from "@/utils";
import { format } from "date-fns";

interface AddTimeAndDayMultiplierFormProps {
  onClose: () => void;
}

export const AddTimeAndDayMultiplierForm = ({ onClose }: AddTimeAndDayMultiplierFormProps) => {
  const [addMultiplier, { isLoading }] = useAddTimeOfDayMultiplierMutation();
  const methods = useForm<TimeAndDayFormData>({
    defaultValues: {
      specificDate: false,
      allDay: false,
      repeatYearly: false,
      treatAsSpecialEvent: true,
      status: true,
    },
    resolver: zodResolver(timeAndDayFormSchema),
  });
  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onSubmit = handleSubmit((data) => {
    let start = data.timeStart;
    let end = data.timeEnd;

    if (data.specificDate && data.dateStart && data.dateEnd) {
      const date = format(new Date(), "yyyy-MM-dd");
      const startTime =  new Date(`${date} ${data.timeStart}`);
      const endTime = new Date(`${date} ${data.timeEnd}`);
      const startDate = data.dateStart.setHours(startTime.getHours(), startTime.getMinutes(), startTime.getSeconds());
      const endDate = data.dateEnd.setHours(endTime.getHours(), endTime.getMinutes(), endTime.getSeconds());
      start = format(startDate, "yyyy-MM-dd'T'HH:mm:ss");
      end = format(endDate, "yyyy-MM-dd'T'HH:mm:ss");
    }

    addMultiplier({
      name: data.name,
      multiplier: Number(data.costMultiplier),
      type: data.specificDate ? "date_time_based" : "time based",
      start_time: start,
      end_time: end,
      is_recurring: data.specificDate ? data.repeatYearly : false,
      is_special_event: data.treatAsSpecialEvent,
      is_active: data.status,
    })
      .unwrap()
      .then(() => {
        addToast("success", "Successfully Added Time & Day");
        onClose();
      })
      .catch((e) => getErrorMessages(e).forEach((m) => addToast("danger", m)));
  });

  return (
    <FormProvider {...methods}>
      <form id="add-multiplier-form" className="relative flex min-h-[550px] w-[515px] max-w-full flex-col" onSubmit={onSubmit}>
        {isLoading && <Loading className="z-20" />}
        <div className="flex-1 space-y-5 py-8">
          <NameField />
          <CostMultiplierField />
          <TimeDateField />
          <TreatAsSpecialEventField />
          <StatusField />
        </div>
        <div className="block">
          <div className="flex justify-end  gap-4">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="add-multiplier-form" disabled={!isDirty}>
              Add Time & Day
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
